import type { ComponentPropsWithoutRef } from 'react'

import { twMerge } from 'tailwind-merge'

import { SweetspotLink } from '@/components/common/Link'

import { buildRecipientId } from '@/lib/usaspending/recipientId'

/**
 * Build the full pathname for a recipient detail page.
 *
 * Accepts either a pre-built recipient ID (`hash-level`) or a raw hash
 * with an optional level. Returns `null` when the input is missing.
 *
 * Useful for cases where you need the URL as a string (e.g. passing to
 * a generic component's `href` prop) rather than rendering a link.
 */
export function recipientHref(
  hash: string | null | undefined,
  level?: string | null,
): string | null {
  const id = buildRecipientId(hash, level)
  if (!id) return null
  return `/recipients/usaspending/${id}`
}

/**
 * RecipientLink
 *
 * Drop-in replacement for `<Link href="/recipients/usaspending/...">` with
 * two key behaviours baked in:
 *
 * 1. **`prefetch={false}`** — recipient detail pages are ISR-generated and
 *    data-heavy (3 prefetch queries each). Letting Next.js eagerly prefetch
 *    every visible link on list/search pages would hammer the backend.
 *
 * 2. **Centralised URL construction** — callers pass `hash` (+ optional
 *    `level`) and the component calls `buildRecipientId` internally.
 *
 * All remaining `<Link>` props (`className`, `target`, `children`, …) are
 * forwarded as-is.
 *
 * @example
 * ```tsx
 * <RecipientLink hash={recipient.id} className="hover:underline">
 *   {recipient.name}
 * </RecipientLink>
 *
 * <RecipientLink hash={hash} level="P" target="_blank">
 *   View parent
 * </RecipientLink>
 * ```
 */
export function RecipientLink({
  hash,
  level,
  children,
  ...rest
}: {
  /** Recipient hash or pre-built recipient ID (`hash-level`). */
  hash: string
  /** Recipient level (P, C, R). Omit if `hash` already includes it. */
  level?: string | null
} & Omit<ComponentPropsWithoutRef<typeof SweetspotLink>, 'href'>) {
  const id = buildRecipientId(hash, level)!
  const className = twMerge('w-fit', rest.className)
  return (
    <SweetspotLink
      href={`/recipients/usaspending/${id}`}
      prefetch={false}
      className={className}
      {...rest}
    >
      {children}
    </SweetspotLink>
  )
}
